import { request, requestWithMsg, MsgResult } from './request';
import { DeleteCascadePayload, RelatedGroup, fetchDeletePreview } from './cascade';

export interface DictEntry {
    id: number;
    uuid: string;
    category: string;
    scopeKey: string;
    code: string;
    label: string;
    icon?: string;
    sort: number;
    isEnabled: boolean;
    createdBy: number;
    updatedBy: number;
    createdAt?: number;
    updatedAt?: number;
}

const BASE = '/v1';

/** 列出某类字典. scope 给字典加上下文限定 (例 process_type 类目 scope=laser-frame 拿激光框机的). */
export function listDict(
    category: string,
    opts: { scope?: string; includeDisabled?: boolean } = {},
): Promise<DictEntry[]> {
    return request<DictEntry[]>(`${BASE}/dict/list`, {
        method: 'POST', dedupe: true,
        body: {
            category,
            scope: opts.scope || '',
            includeDisabled: !!opts.includeDisabled,
        },
    });
}

// dict 写接口: 统一走审批流 (message 含"已提交审批", data={taskCode}).
export function createDict(
    category: string,
    payload: { scopeKey?: string; code: string; label: string; icon?: string; sort?: number; isEnabled?: boolean },
): Promise<MsgResult<{ id?: number; category?: string; scopeKey?: string; code?: string; taskCode?: string }>> {
    return requestWithMsg(`${BASE}/dict/create`, { method: 'POST', body: { category, ...payload } });
}

export function updateDict(
    category: string,
    code: string,
    payload: { label?: string; icon?: string; sort?: number; isEnabled?: boolean },
    opts: { scope?: string } = {},
): Promise<MsgResult<{ taskCode?: string } | null>> {
    // 业务键 (category, scope, code) 全走 body: 后端只从 body 绑, scope 挂 query 会丢
    // → scope_key 非空的条目 (process_type 按框架分) 匹配 0 行, 工单回放报 dict_entry not found
    return requestWithMsg(`${BASE}/dict/update`, {
        method: 'POST',
        body: { category, code, scope: opts.scope || '', ...payload },
    });
}

export function deleteDict(
    category: string,
    code: string,
    opts: { scope?: string } = {},
): Promise<MsgResult<{ taskCode?: string } | null>> {
    // 同 updateDict: scope 必须在 body 里, 否则删的是 scope_key='' 的条目 (匹配不到)
    return requestWithMsg(`${BASE}/dict/delete`, {
        method: 'POST',
        body: { category, code, scope: opts.scope || '' },
    });
}

// --- 工艺架构 CRUD (兼用) ---

export interface ProcessArch {
    id: number;
    code: string;
    label: string;
    icon?: string;
    sort: number;
    isEnabled: boolean;
    /** 该框架参数表名 (param_laser_frame/...); 后端按 code 派生, 只读透出. */
    paramTable?: string;
    /** 材料键粒度: xcode / dual_xcode / category_code. */
    materialKeyKind?: string;
}

export function listProcessArchs(): Promise<ProcessArch[]> {
    // 启用中的工艺框架 (展示侧用: process_type 是 scoped 类目, 取名要按框架 scope 拉字典).
    return request<ProcessArch[]>(`${BASE}/process-archs`);
}

let archsCache: Promise<ProcessArch[]> | null = null;

/** 工艺框架列表的模块级缓存 (小静态数据, 全站一次请求; 失败不缓存). */
export function loadProcessArchsCached(): Promise<ProcessArch[]> {
    if (!archsCache) {
        archsCache = listProcessArchs().catch(() => {
            archsCache = null;
            return [] as ProcessArch[];
        });
    }
    return archsCache;
}

export function listProcessArchsAdmin(): Promise<ProcessArch[]> {
    // 字典维护页需全集 (含停用框架, 如占位的刺绣); includeDisabled=1 走 ListProcessArchsAll.
    return request<ProcessArch[]>(`${BASE}/process-archs?includeDisabled=1`);
}

let archsAllCache: Promise<ProcessArch[]> | null = null;

/** 工艺框架全集 (含停用) 的模块级缓存: 加工模式组「适用框架」选项与标签用 (失败不缓存). */
export function loadProcessArchsAllCached(): Promise<ProcessArch[]> {
    if (!archsAllCache) {
        archsAllCache = listProcessArchsAdmin().catch(() => {
            archsAllCache = null;
            return [] as ProcessArch[];
        });
    }
    return archsAllCache;
}

// 工艺架构写接口: 统一走审批流 (message 含"已提交审批", data={taskCode}).
export function createProcessArch(
    // param_table 不传: 后端按 code 派生 (param_<code>).
    payload: {
        code: string;
        label: string;
        icon?: string;
        sort?: number;
        isEnabled?: boolean;
        materialKeyKind?: string;
    },
): Promise<MsgResult<{ id?: number; code?: string; taskCode?: string }>> {
    return requestWithMsg(`${BASE}/process-archs`, { method: 'POST', body: payload });
}

export function updateProcessArch(
    code: string,
    payload: {
        label?: string;
        icon?: string;
        sort?: number;
        isEnabled?: boolean;
        materialKeyKind?: string;
    },
): Promise<MsgResult<{ taskCode?: string } | null>> {
    return requestWithMsg(`${BASE}/process-archs/update`, { method: 'POST', body: { code, ...payload } });
}

export function deleteProcessArch(code: string): Promise<MsgResult<{ taskCode?: string } | null>> {
    return requestWithMsg(`${BASE}/process-archs/delete`, { method: 'POST', body: { code } });
}

// --- 字典类目 (dict_category, 2026-08-09): 类目数据化, 字典维护可自助新增 ---

export interface DictCategory {
    id: number;
    code: string;
    label: string;
    description?: string;
    /** 展示分组 (platform/device/material/param/other): 字典维护页按此归堆 tab */
    grp: string;
    /** 条目是否按工艺架构 scope 分组 (目前仅 process_type) */
    scoped: boolean;
    /** 内置类目 (功能依赖, 禁删) */
    builtin: boolean;
    sort: number;
    isEnabled: boolean;
}

export function listDictCategories(includeDisabled = false): Promise<DictCategory[]> {
    return request<DictCategory[]>(`${BASE}/dict-categories`, {
        query: { includeDisabled: includeDisabled ? 'true' : '' },
    });
}

/** 类目展示分组词表 (后端 dict.CategoryGroups; 顺序即展示顺序). */
export interface DictCategoryGroup {
    code: string;
    label: string;
    hint: string;
}

export function listDictCategoryGroups(): Promise<DictCategoryGroup[]> {
    return request<DictCategoryGroup[]>(`${BASE}/dict-category-groups`);
}

export function createDictCategory(p: {
    code: string;
    label: string;
    description?: string;
    grp: string;
    sort?: number;
}): Promise<MsgResult<{ id?: number }>> {
    return requestWithMsg(`${BASE}/dict-categories`, { method: 'POST', body: p });
}

export function updateDictCategory(
    code: string,
    p: { label?: string; description?: string; grp?: string; sort?: number; isEnabled?: boolean },
): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/dict-categories/update`, { method: 'POST', body: { code, ...p } });
}

/** 删类目前的预检 (2026-09-08): 条目明细 (随类目一并删) + 被字段枚举来源/矩阵行来源引用的阻塞项. */
export function fetchDictCategoryDeletePreview(code: string): Promise<RelatedGroup[]> {
    return fetchDeletePreview(`${BASE}/dict-categories/delete-preview`, { code });
}

/** 删类目 (连带其全部条目); payload 是预检快照, 进审批工单展示影响面. */
export function deleteDictCategory(code: string, payload?: DeleteCascadePayload): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/dict-categories/delete`, { method: 'POST', body: { code, ...(payload || {}) } });
}
